<script module>
  export const DRUG_SELECTION_KEY = 'drugSelection';
</script>

<script>
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import InfoTile from '../InfoTile/InfoTile.svelte';
  import BaseButton from '../BaseButton/BaseButton.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import { getDrug } from '../../stores/drug';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { overpanelMap, closeOverpanel } from '../../stores/overpanelControl';
  import currentLocation from '../../stores/currentLocation';
  
  let {
    title,
    brandLabel,
    genericLabel,
    hrefTemplate,
    infoTiles = [],
    drugs = [],
    dataTestId = 'drug-selection-overpanel',
    closeCallback = () => {}
  } = $props();

  const handleClick = () => {
    closeOverpanel(DRUG_SELECTION_KEY);
    closeCallback();
  };

  const getCombinedDrugs = () => {
    let variableLink = hrefTemplate;
    let query = [];

    if ($overpanelMap.size > 1) {
      query.push('showDrugPane=true');
    }

    if (query.length) {
      variableLink = `${variableLink}?${query.join('&')}`;
    }
    return [...drugs, selectedDrug].map((drug) => {
      const drugType = drug.isBrand ? 'brand' : 'generic';
      const componentProps = drug.isSelected
        ? { onClick: handleClick }
        : { url: variableLink.replace(/{{SLUG}}/, drug.slug) };
      return { ...drug, drugType, componentProps };
    });
  };

  const { name, formalName, slug, genericSlug, price } = getDrug();

  const selectedDrug = {
    name,
    formalName,
    slug,
    price,
    isBrand: slug !== genericSlug,
    isSelected: true,
  };

  const showInfoTiles = !!infoTiles.length;

  let combinedDrugs = $state(getCombinedDrugs());

  currentLocation.subscribe(() => {
    combinedDrugs = getCombinedDrugs();
  });

  const handleWindowClick = (e) => {
    if (!e || !e.target) return;

    const links = document.querySelectorAll('.drug-selection-overpanel-active-link');

    const elem = e.target;

    if (links.length > 0 && Array.from(links).find((link) => link.contains(elem) || link === elem)) {
      closeOverpanel(DRUG_SELECTION_KEY);
    }
  };
</script>

<svelte:window onclick={handleWindowClick} />

{#if $overpanelMap.has(DRUG_SELECTION_KEY)}
  <OverpanelControl {title} {closeCallback} overpanelKey={DRUG_SELECTION_KEY}>
    <div class="flex flex-col justify-between bg-gray-mercury h-full w-full" data-testid={dataTestId}>
      <div class="px-2 pt-2 overflow-auto" class:pb-6={!showInfoTiles} class:pb-8={showInfoTiles}>
        {#each combinedDrugs as drug, i}
          {@const SvelteComponent = drug.isSelected ? BaseButton : BaseLink}
          <SvelteComponent
            className="relative w-full block mb-2 rounded-lg group active:shadow-active shadow-y-sm pseudo-shadow-tight-hover {!drug.isSelected
              ? 'drug-selection-overpanel-active-link'
              : ''}"
            dataTestId="{dataTestId}-tile-{drug.isSelected ? 'selected' : 'unselected'}-{i}"
            {...drug.componentProps}
          >
            <div class="w-full flex flex-col grow">
              <div
                class="flex w-full justify-between items-center p-3 pb-2 rounded-t-lg"
                class:bg-white={!drug.isSelected}
                class:bg-blue-cerulean={drug.isSelected}
              >
                <div class="text-left">
                  <p
                    class="text-base font-medium tracking-wide"
                    class:text-white={drug.isSelected}
                    class:text-blue-navy={!drug.isSelected}
                  >
                    {drug.name}
                  </p>
                  <p class="text-sm" class:text-white={drug.isSelected} class:text-gray-charcoal={!drug.isSelected}>
                    {drug.formalName}
                  </p>
                </div>
                {#if drug.price}
                  {#if !isNaN(drug.price.replace(',', ''))}
                    <span
                      class="shrink-0 text-right text-base font-medium text-blue-cerulean hover-hover:group-hover:hidden"
                    >
                      <sup class="text-2xs -top-2">$</sup>
                      {drug.price}
                    </span>
                  {:else}
                    <span
                      class="shrink-0 text-right text-base font-medium text-blue-cerulean hover-hover:group-hover:hidden"
                    >
                      {drug.price}
                    </span>
                  {/if}
                {/if}
                <svg
                  class="h-6 w-6 fill-current hover-hover:group-hover:block"
                  class:text-white={drug.isSelected}
                  class:text-gray-light-silver={!drug.isSelected}
                  class:hidden={!drug.isSelected}
                  class:block={drug.isSelected}
                  role="presentation"
                  data-testid="drug-selection-overpanel-tile-check-{drug.isSelected ? 'selected' : 'unselected'}"
                >
                  <use xlink:href="{SPRITESHEET_URL}#check" />
                </svg>
              </div>
              <div
                class="py-1 text-center uppercase rounded-b-lg"
                class:bg-blue-very-light={drug.drugType === 'generic'}
                class:text-blue-navy={drug.drugType === 'generic'}
                class:bg-gray-mercury={drug.drugType !== 'generic'}
                class:text-gray-charcoal={drug.drugType !== 'generic'}
              >
                <p
                  class="text-xs leading-normal text-center tracking-wide font-medium uppercase"
                  data-testid="drug-selection-overpanel-{drug.drugType}-label"
                >
                  {drug.drugType !== 'generic' ? brandLabel : genericLabel}
                </p>
              </div>
            </div>
          </SvelteComponent>
        {/each}
      </div>
      <div>
        {#if showInfoTiles}
          {#each infoTiles as ad, i}
            <InfoTile {...ad} dataTestId="drug-selection-overpanel-info-tile-{i}" />
          {/each}
        {/if}
      </div>
    </div>
  </OverpanelControl>
{/if}
